import { Flex, Skeleton, Text, useBreakpointValue } from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PhoneNumber } from '@mangovoice/types'

import { useReportingI18n } from '../i18n'
import {
  DropdownMultiSelect,
  MultiSelectItem,
} from './dropdown/DropdownMultiSelect'
import { MenuPlacement } from './dropdown/Menu'
import { PhoneNumberPills } from './PhoneNumberPills'

interface PhoneNumberMultiDropdownProps {
  selectedPhoneNumbers: PhoneNumber[]
  phoneNumbers: PhoneNumber[]
  isPhoneNumbersLoading: boolean
  onSelectionChange?: (phones: PhoneNumber[]) => void
  placeholder?: string
  width?: string
  maxSelection?: number
}

export const PhoneNumberMultiDropdown = ({
  selectedPhoneNumbers,
  phoneNumbers,
  isPhoneNumbersLoading,
  onSelectionChange,
  placeholder,
  width = '18rem',
  maxSelection = 50,
}: PhoneNumberMultiDropdownProps) => {
  const { text } = useReportingI18n()
  const resolvedPlaceholder =
    placeholder ?? text('phoneNumberMultiDropdown.allNumbers', 'All Numbers')
  const isLoading = isPhoneNumbersLoading
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const onSelectionChangeRef = useRef(onSelectionChange)

  const maxVisiblePills = useBreakpointValue(
    {
      base: 1,
      sm: 1,
      md: 2,
      lg: 3,
      xl: 4,
    },
    {
      fallback: 'md',
    },
  )

  onSelectionChangeRef.current = onSelectionChange

  const [pendingChanges, setPendingChanges] = useState<PhoneNumber[] | null>(
    null,
  )

  const displayPhones = pendingChanges ?? selectedPhoneNumbers

  const selectedValues = useMemo(() => {
    return displayPhones.map((p) => p.id.toString())
  }, [displayPhones])

  const sortOrderIds = useMemo(() => {
    return new Set(selectedPhoneNumbers.map((p) => p.id.toString()))
  }, [selectedPhoneNumbers])

  const selectItems: MultiSelectItem<string>[] = useMemo(() => {
    const mapped = phoneNumbers.map((p) => {
      const name = p.name || p.number_for_display || p.number
      const displayNumber = p.number_for_display || p.number
      const label = name

      const searchLabel = `${name} ${displayNumber} ${p.number}`.toLowerCase()

      const content = (
        <Flex direction="column" gap="0.125rem">
          <Text
            fontSize="14px"
            fontWeight="400"
            color="fg.default"
            lineHeight="1.2"
          >
            {name}
          </Text>
          <Text
            fontSize="12px"
            fontWeight="400"
            color="fg.muted"
            lineHeight="1.2"
          >
            {displayNumber}
          </Text>
        </Flex>
      )

      return {
        value: p.id.toString(),
        label: label,
        searchLabel: searchLabel,
        content: content,
      }
    })

    const selected: MultiSelectItem<string>[] = []
    const unselected: MultiSelectItem<string>[] = []
    for (const item of mapped) {
      ;(sortOrderIds.has(item.value) ? selected : unselected).push(item)
    }
    return [...selected, ...unselected]
  }, [phoneNumbers, sortOrderIds])

  const handleSelectionChange = useCallback(
    (values: string[]) => {
      const selectedPhones = values
        .map((id) => phoneNumbers.find((p) => p.id.toString() === id))
        .filter((p): p is PhoneNumber => p !== undefined)

      setPendingChanges(selectedPhones)

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        setPendingChanges(null)
        onSelectionChangeRef.current?.(selectedPhones)
      }, 800)
    },
    [phoneNumbers],
  )

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
    }
  }, [selectedPhoneNumbers])

  const handleRemovePhone = useCallback(
    (phoneToRemove: PhoneNumber) => {
      const updatedPhones = displayPhones.filter(
        (p) => p.id !== phoneToRemove.id,
      )

      setPendingChanges(updatedPhones)

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        setPendingChanges(null)
        onSelectionChangeRef.current?.(updatedPhones)
      }, 800)
    },
    [displayPhones],
  )

  const handleClearAll = useCallback(() => {
    setPendingChanges([])
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      setPendingChanges(null)
      onSelectionChangeRef.current?.([])
    }, 800)
  }, [])

  if (isLoading) {
    return <Skeleton width={width} height="40px" borderRadius="md" />
  }

  return (
    <Flex gap="0.5rem" alignItems="center" flexWrap="wrap">
      <Flex width={width}>
        <DropdownMultiSelect
          placeholder={resolvedPlaceholder}
          selectedValues={selectedValues}
          items={selectItems}
          onSelectionChange={handleSelectionChange}
          searchable
          maxSelection={maxSelection}
          menuOptions={{
            width: '18.75rem',
            maxHeight: '35vh',
            placement: MenuPlacement.BottomRight,
          }}
        />
      </Flex>

      <PhoneNumberPills
        selectedPhoneNumbers={displayPhones}
        onRemovePhone={handleRemovePhone}
        onClearAll={handleClearAll}
        maxVisiblePills={maxVisiblePills}
      />
    </Flex>
  )
}
